import {useState} from 'react'
import {useAuth} from '@/context/AuthContext.jsx'
import {usePreciseLocation} from '@/hooks/usePreciseLocation.js'
import {useLocationAccess} from '@/hooks/useLocationAccess'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'

/** Closed for this browser session — it comes back next visit, not on every page. */
const DISMISS_KEY = 'hopn:precise-location-dismissed'

const readDismissed = () => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1' } catch { return false }
}

/**
 * THE LOCATION MODAL, passenger side — the same modal as the driver portal's
 * (`LocationPrompt`): same title, same layout, same one button, passenger wording. Shown to
 * a signed-in passenger whose location is BLOCKED, or shared as Approximate on mobile.
 * Not for "prompt" — the screens that need location make the browser ask by itself.
 * The allowed/blocked answer is useLocationAccess's — the one shared check.
 *
 * ⚠️ NOT A RESTRICTION. Unlike the driver's, it can be closed, and stays closed for the
 * browser session — a passenger is never blocked by it; precise location only makes pickup
 * guidance better.
 */
export default function PreciseLocationNotice() {
    const {isAuthenticated} = useAuth()
    const [dismissed, setDismissed] = useState(readDismissed)
    const {state: permission} = useLocationAccess()
    const {isApproximate} = usePreciseLocation({enabled: isAuthenticated && !dismissed, granted: permission === 'granted'})

    const dismiss = () => {
        try { sessionStorage.setItem(DISMISS_KEY, '1') } catch { /* private mode */ }
        setDismissed(true)
    }

    const open = isAuthenticated && !dismissed
        && (permission === 'denied' || (permission === 'granted' && isApproximate))

    return (
        <Modal
            isOpen={open}
            onClose={dismiss}
            className="md:max-w-md"
            footer={
                <button
                    type="button"
                    className="hopn-add-passenger-button"
                    onClick={() => window.location.reload()}
                >
                    Enabled reload page
                </button>
            }
        >
            <div className="flex flex-col gap-4 text-sm text-(--text-secondary) max-md:gap-3 max-md:text-xs">
                <h2 className="text-2xl font-semibold text-(--text-primary) max-md:text-lg">
                    Share your location (precise)
                </h2>
                <p className="mb-0">
                    We use your location to guide you to your pickup point and help your driver
                    find you. Without it, your driver cannot see where you are waiting.
                </p>
                <p className="mb-0 rounded-[10px] bg-[#defff4] px-4 py-3 text-[#201e20] max-md:px-3 max-md:py-2">
                    Keep this page open during your pickup. Browsers stop sharing location
                    when the tab is in the background or the phone is locked.
                </p>
            </div>
        </Modal>
    )
}
